import { ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/core/auth/useAuth";
import { useModuleAccess } from "@/core/permissions/useModuleAccess";
import { isAdminAreaOnlyUser, isAdminAreaBaseRoute, isPathAllowedForAdminArea } from "@/core/permissions/adminAreaAccess";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  requiredModule?: string;
}

const ProtectedRoute = ({ children, requiredRoles = [], requiredModule }: ProtectedRouteProps) => {
  const { user, isLoading, hasRole, mfaRequired, mfaSatisfied } = useAuth();
  const { hasModule, isLoading: modulesLoading } = useModuleAccess();
  const navigate = useNavigate();
  const location = useLocation();

  // Gate MFA: l'utente con ruolo che richiede MFA e sessione non-aal2 NON deve vedere
  // alcun contenuto protetto. Calcolato sincronicamente → nessun flash della dashboard.
  // Le pagine MFA stesse sono fuori da ProtectedRoute, quindi non si auto-bloccano.
  const needsMfaGate = mfaRequired && !mfaSatisfied;

  // Pre-compute all role checks as primitive booleans.
  // hasRole is NOT wrapped in useCallback (bare function in useAuthState),
  // so we must never put it in useEffect deps — it would fire every render.
  const isOnlyAdminAreaUser = isAdminAreaOnlyUser(hasRole);
  const hasRequiredRole = requiredRoles.length === 0 || requiredRoles.some(role => hasRole(role));
  const hasRequiredModule = !requiredModule || modulesLoading || hasModule(requiredModule);

  // Utente solo-area-amministrazione: oltre a /amministrazione può raggiungere le
  // route dei moduli che gli sono stati concessi (es. /shop con il modulo "shop").
  // Finché i moduli caricano non si decide nulla: un refresh diretto su /shop
  // valuterebbe hasModule() a false e rimbalzerebbe su /amministrazione.
  const adminAreaGatePending = isOnlyAdminAreaUser && modulesLoading &&
    !isAdminAreaBaseRoute(location.pathname);
  const isAdminAreaPathBlocked = isOnlyAdminAreaUser && !modulesLoading &&
    !isPathAllowedForAdminArea(location.pathname, hasModule);

  useEffect(() => {
    // Se non stiamo ancora caricando e non c'è un utente, reindirizza al login.
    // Area PWA: gli operatori accedono con il PIN su /device (device pairing),
    // non con email+password → returnTo li riporta alla pagina richiesta.
    if (!isLoading && !user) {
      if (location.pathname.startsWith("/pwa")) {
        const returnTo = encodeURIComponent(location.pathname + location.search);
        navigate(`/device?returnTo=${returnTo}`, { replace: true });
      } else {
        navigate("/auth", { replace: true });
      }
      return;
    }

    // Gate MFA: se serve il secondo fattore, reindirizza alla verifica PRIMA di
    // qualsiasi altro check. La pagina /auth/mfa-verify decide poi setup vs verify.
    if (!isLoading && user && needsMfaGate) {
      navigate("/auth/mfa-verify", { replace: true });
      return;
    }

    // Utenti solo-amministrazione: solo /amministrazione e i moduli concessi
    if (!isLoading && user && isAdminAreaPathBlocked) {
      navigate("/amministrazione", { replace: true });
      return;
    }

    // Se ci sono ruoli richiesti e l'utente non ha nessuno dei ruoli richiesti, reindirizza
    if (!isLoading && user && !hasRequiredRole) {
      if (isOnlyAdminAreaUser) {
        navigate("/amministrazione", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }

    // Module access check (only if requiredModule is specified and modules are loaded)
    if (!isLoading && !modulesLoading && user && requiredModule && !hasRequiredModule) {
      const fallback = location.pathname.startsWith("/amministrazione") ? "/amministrazione" : "/";
      navigate(fallback, { replace: true });
    }
  }, [user, isLoading, navigate, isOnlyAdminAreaUser, isAdminAreaPathBlocked, hasRequiredRole, location.pathname, location.search, requiredModule, hasRequiredModule, modulesLoading, needsMfaGate]);

  // Mostra un caricamento durante il controllo dell'autenticazione o dei moduli
  if (isLoading || (requiredModule && modulesLoading) || adminAreaGatePending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary"></div>
      </div>
    );
  }

  // GATE MFA — flicker-free: se serve il secondo fattore, NON renderizzare mai i children.
  // Mostra il loader mentre l'effect reindirizza a /auth/mfa-verify. Questo impedisce
  // il flash della dashboard sia post-login sia su refresh diretto di una route protetta.
  if (user && needsMfaGate) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-primary"></div>
      </div>
    );
  }

  // Non mostrare nulla se non c'è un utente o ruoli non corrispondono
  if (!user || !hasRequiredRole) {
    return null;
  }
  // Non mostrare nulla se modulo non accessibile
  if (!hasRequiredModule) {
    return null;
  }

  // Non mostrare nulla se utente solo-amministrazione sta accedendo a pagina non autorizzata
  if (isAdminAreaPathBlocked) {
    return null;
  }

  // Mostra i children se l'utente è autenticato e ha i ruoli richiesti
  return <>{children}</>;
};

export default ProtectedRoute;
